import React from 'react';
import { useSelector } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import {
  ExternalLink,
  H1,
  H2,
  H3,
  IconSpinner,
  LayoutSingleColumn,
  NamedLink,
  Page,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { IconBox, IconDocument, IconEnvelope } from './StepIcons';
import css from './AngreskjemaPage.module.css';

const SUPPORT_EMAIL = 'post@zuprize.no';

// "Slik bruker du angreskjemaet": Guro's three steps, in order.
const STEPS = [
  { key: 'step1', Icon: IconEnvelope },
  { key: 'step2', Icon: IconDocument },
  { key: 'step3', Icon: IconBox },
];

// "Greit å vite": each sentence its own key, so a wording change touches one.
const GOOD_TO_KNOW = [
  { key: 'onlyPartners', sentences: ['onlyPartnersSentence1', 'onlyPartnersSentence2'] },
  { key: 'refund', sentences: ['refundSentence1', 'refundSentence2', 'refundSentence3'] },
  { key: 'help', sentences: ['helpSentence1', 'helpSentence2'] },
];

/**
 * One partner: its logo (or initial) and name, then "Last ned angreskjema"
 * (only when the partner has a form) and "Se profil".
 */
const PartnerTile = ({ partner }) => (
  <div className={css.tile}>
    <span className={css.logo}>
      {partner.logo ? (
        <img className={css.logoImage} src={partner.logo} alt="" />
      ) : (
        <span className={css.logoInitial} aria-hidden="true">
          {partner.displayName.slice(0, 1)}
        </span>
      )}
    </span>
    <span className={css.name}>{partner.displayName}</span>
    <span className={css.actions}>
      {/* The partner's own form, in a new tab. */}
      {partner.angreskjemaUrl ? (
        <ExternalLink className={css.action} href={partner.angreskjemaUrl}>
          <FormattedMessage id="AngreskjemaPage.downloadForm" />
        </ExternalLink>
      ) : null}
      <NamedLink
        className={css.action}
        name="ProfilePage"
        params={{ id: partner.username || partner.id }}
      >
        <FormattedMessage id="AngreskjemaPage.seeProfile" />
      </NamedLink>
    </span>
  </div>
);

/** "Slik bruker du angreskjemaet": three numbered cards. */
const HowToUse = () => (
  <section className={css.steps} aria-labelledby="angreskjema-steps">
    <div className={css.stepsHeader}>
      <H2 id="angreskjema-steps" className={css.stepsHeading}>
        <FormattedMessage id="AngreskjemaPage.stepsHeading" />
      </H2>
      <p className={css.stepsSubtitle}>
        <FormattedMessage id="AngreskjemaPage.stepsSubtitle" />
      </p>
    </div>
    <ol className={css.stepList}>
      {STEPS.map(({ key, Icon }, index) => (
        <li key={key} className={css.step}>
          <span className={css.stepNumber} aria-hidden="true">
            {index + 1}
          </span>
          <span className={css.stepIconDisc}>
            <Icon className={css.stepIcon} />
          </span>
          <H3 as="h3" className={css.stepTitle}>
            <FormattedMessage id={`AngreskjemaPage.${key}Title`} />
          </H3>
          <p className={css.stepText}>
            <FormattedMessage id={`AngreskjemaPage.${key}Text`} />
          </p>
        </li>
      ))}
    </ol>
  </section>
);

/** "Greit å vite": three short sections, each sentence its own key. */
const GoodToKnow = () => {
  const values = {
    termsLink: (
      <NamedLink className={css.inlineLink} name="UserTermsPage">
        <FormattedMessage id="AngreskjemaPage.refundTermsLink" />
      </NamedLink>
    ),
    email: (
      <a className={css.inlineLink} href={`mailto:${SUPPORT_EMAIL}`}>
        {SUPPORT_EMAIL}
      </a>
    ),
  };
  return (
    <section className={css.goodToKnow} aria-labelledby="angreskjema-good-to-know">
      <H2 id="angreskjema-good-to-know" className={css.goodToKnowHeading}>
        <FormattedMessage id="AngreskjemaPage.goodToKnowHeading" />
      </H2>
      {GOOD_TO_KNOW.map(({ key, sentences }) => (
        <div key={key} className={css.goodToKnowItem}>
          <H3 as="h3" className={css.goodToKnowTitle}>
            <FormattedMessage id={`AngreskjemaPage.${key}Title`} />
          </H3>
          <p className={css.goodToKnowText}>
            {sentences.map((id, i) => (
              <React.Fragment key={id}>
                {i > 0 ? ' ' : null}
                <FormattedMessage id={`AngreskjemaPage.${id}`} values={values} />
              </React.Fragment>
            ))}
          </p>
        </div>
      ))}
    </section>
  );
};

/**
 * Angreskjema (/angreskjema): the way to each Zuprize Partner's own
 * withdrawal form, linked from the footer.
 *
 * Every verified partner is a tile with its logo and name, "Last ned
 * angreskjema" (the partner's own form, `angreskjemaUrl` on its account,
 * operator-only metadata, in a new tab; only when it has one) and "Se profil".
 * Under the partners: how to use the form in three steps, and "Greit å vite".
 * With no partners, the intro explains that withdrawal applies to purchases
 * from partners only.
 *
 * @component
 * @returns {JSX.Element}
 */
const AngreskjemaPage = () => {
  const intl = useIntl();
  const { partners = [], loadInProgress } = useSelector(state => state.AngreskjemaPage || {});
  const scrollingDisabled = useSelector(isScrollingDisabled);

  return (
    <Page
      title={intl.formatMessage({ id: 'AngreskjemaPage.title' })}
      description={intl.formatMessage({ id: 'AngreskjemaPage.description' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <H1 className={css.heading}>
            <FormattedMessage id="AngreskjemaPage.heading" />
          </H1>
          <p className={css.intro}>
            <FormattedMessage
              id={partners.length > 0 ? 'AngreskjemaPage.intro' : 'AngreskjemaPage.noPartners'}
            />
          </p>

          {loadInProgress && partners.length === 0 ? (
            <div className={css.loading}>
              <IconSpinner />
            </div>
          ) : null}

          {partners.length > 0 ? (
            <ul className={css.tiles}>
              {partners.map(partner => (
                <li key={partner.id}>
                  <PartnerTile partner={partner} />
                </li>
              ))}
            </ul>
          ) : null}

          <HowToUse />
          <GoodToKnow />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default AngreskjemaPage;
